import { Application, Sprite, Graphics, Texture, Container, utils } from 'pixi.js';
import { gsap } from 'gsap';
import { debounce } from "../../../utils";

class HeroGiftGuide {
    constructor(el) {
        this.el = el;

        this.width = this.el.offsetWidth;
        this.height = this.el.offsetHeight;
        this.setSizes();
        this.prevWidth = this.width;
        this.prevHeight = this.height;

        this.binds();
        this.onResize();
        this.initApp();
        this.loadAssets();
        this.initEvents();
    }

    initEvents() {
        window.addEventListener('resize', debounce(this.onResize.bind(this), 100));
        this.app.view.addEventListener('click', this.handleCanvasClick.bind(this));
    }

    binds() {
        this.onResize = this.onResize.bind(this);
    }

    setSizes() {
        if (this.app) {
            this.app.renderer.resize(this.width, this.height);
        }
    }

    onResize() {
        this.width = this.el.offsetWidth;
        this.height = this.el.offsetHeight;

        if (this.width !== this.prevWidth || this.height !== this.prevHeight) {
            this.setSizes();
            this.prevWidth = this.width;
            this.prevHeight = this.height;
            this.handleResize();
        }
    }

    handleResize() {
        if(this.lettersContainer) {
            this.lettersContainer.x = this.width / 2;
            this.lettersContainer.y = this.height / 2;
            this.lettersContainer.pivot.x = this.title.width / 2;
            this.lettersContainer.pivot.y = this.title.height / 2;
        }

        if(this.x1) {
            this.x1.x = this.width / 2;
            this.x1.y = this.height / 2;
        }

        if(this.x2) {
            this.x2.x = this.width / 2;
            this.x2.y = this.height / 2;
        }

        if(this.x3) {
            this.x3.x = this.width / 2;
            this.x3.y = this.height / 2;
        }

        if(this.shadow) {
            this.shadow.x = this.width / 2;
            this.shadow.y = this.height / 2;
        }

        if(this.shadowBg) {
            this.shadowBg.x = this.width / 2;
            this.shadowBg.y = this.height / 2;
        }

        if(this.title) {
            this.title.x = this.width / 2;
            this.title.y = this.height / 2;
        }

        if(this.glow) {
            this.glow.x = this.width / 2;
            this.glow.y = this.height / 2;
        }

        this.calculateTitleScale();

        if(this.year2023) {
            this.year2023.x = this.titleScale === 1
                ? this.title.width / 4 + this.width / 2 + 30
                : this.width * .81;
            this.year2023.y = this.height / 2;
            this.year2023.scale.set(this.titleScale / 2);
        }

        this.resizeSnowfall()
        this.resizeGradientBackground();
    }

    initApp() {
        utils.skipHello();
        this.app = new Application({
            width: this.width,
            height: this.height,
            resolution: 2,
            autoDensity: true,
            antialias: true,
            backgroundColor: 0x000000,
        });

        this.app.stage.sortableChildren = true;
        this.el.appendChild(this.app.view);
    }

    loadAssets() {
        this.app.loader
            .add('letters', `${window.assets_url}/images/gift-guide/letters1.png`)
            .add('shadowbg', `${window.assets_url}/images/gift-guide/gift-shadow-bg.png`)
            .add('shadow', `${window.assets_url}/images/gift-guide/gift-title-shadow.png`)
            .add('glow', `${window.assets_url}/images/gift-guide/gift-title-glow.png`)
            .add('title', `${window.assets_url}/images/gift-guide/gift-title.png`)
            .add('x1', `${window.assets_url}/images/gift-guide/header-neon-1.png`)
            .add('x2', `${window.assets_url}/images/gift-guide/header-neon-2.png`)
            .add('x3', `${window.assets_url}/images/gift-guide/header-neon-3.png`)
            .add('2023', `${window.assets_url}/images/gift-guide/gift-year-2023.png`)

        this.app.loader.load((loader, resources) => {
            this.onAssetsLoaded(resources);
        });
    }

    onAssetsLoaded(resources) {
        this.resources = resources;
        this.initScene();
    }

    initScene() {
        this.scene = new Container();
        this.app.stage.addChild(this.scene);
        this.scene.pivot.x = this.width / 2;
        this.scene.pivot.y = this.height / 2;
        this.scene.x = this.width / 2;
        this.scene.y = this.height / 2;

        this.lettersContainer = new Container();
        this.addHeaderX();
        this.addGradientBackground();
        this.addShadowBg();
        this.addShadow();
        this.addTitle();
        this.initLetters();
        this.addGlow();
        this.calculateTitleScale();
        this.add2023();
        this.animateLetters();
        this.initSnowfall();
    }

    handleCanvasClick(event) {
        const clickX = event.offsetX;
        const clickY = event.offsetY;
        const numFlakes = Math.floor(Math.random() * 4) + 5;

        for (let i = 0; i < numFlakes; i++) {
            this.addAdditionalSnowflake(clickX, clickY);
        }
    }

    addAdditionalSnowflake(x, y) {
        let graphics = new Graphics();
        graphics.beginFill(0xFFFFFF, 0.8);
        let radius = Math.random() * (this.radiusMax - this.radiusMin) + this.radiusMin;
        graphics.drawCircle(0, 0, radius);
        graphics.endFill();

        let angle = Math.random() * Math.PI * 2;
        let speed = Math.random() * 5 + 2;

        let particle = {
            graphics: graphics,
            x: x,
            y: y,
            r: radius,
            d: Math.random() * this.snowfall_max,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed
        };

        this.snowfallContainer.addChild(graphics);
        this.snowfall.push(particle);

        const additionalUpdate = () => {
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.vx *= 0.97;
            particle.vy *= 0.97;

            if (Math.abs(particle.vx) < 0.01 && Math.abs(particle.vy) < 0.01) {
                this.app.ticker.remove(additionalUpdate);
            }

            particle.graphics.x = particle.x;
            particle.graphics.y = particle.y;

            if (particle.y > this.height || particle.x < -this.width || particle.x > this.width) {
                this.app.ticker.remove(additionalUpdate);
                this.snowfallContainer.removeChild(particle.graphics);
            }
        };

        this.app.ticker.add(additionalUpdate);
    }

    addX1() {
        this.x1 = new Sprite(this.resources.x1.texture);
        this.x1.width = this.resources.x1.texture.orig.width / 2;
        this.x1.height = this.resources.x1.texture.orig.height / 2;
        this.x1.x = this.width / 2;
        this.x1.y = this.height / 2;
        this.x1.anchor.set(0.5);
        this.x1.alpha = 0;
        this.x1.zIndex = 0;
        this.scene.addChild(this.x1);
    }

    addX2() {
        this.x2 = new Sprite(this.resources.x2.texture);
        this.x2.width = this.resources.x2.texture.orig.width / 2;
        this.x2.height = this.resources.x2.texture.orig.height / 2;
        this.x2.x = this.width / 2;
        this.x2.y = this.height / 2;
        this.x2.anchor.set(0.5);
        this.x2.alpha = 0;
        this.x2.zIndex = 0;
        this.scene.addChild(this.x2);
    }

    addX3() {
        this.x3 = new Sprite(this.resources.x3.texture);
        this.x3.width = this.resources.x3.texture.orig.width / 2;
        this.x3.height = this.resources.x3.texture.orig.height / 2;
        this.x3.x = this.width / 2;
        this.x3.y = this.height / 2;
        this.x3.anchor.set(0.5);
        this.x3.alpha = 0;
        this.x3.zIndex = 0;
        this.scene.addChild(this.x3);
    }

    addHeaderX() {
        this.addX1();
        this.addX2();
        this.addX3();
    }

    add2023() {
        this.year2023 = new Sprite(this.resources['2023'].texture);
        this.year2023.width = this.resources['2023'].texture.orig.width / 2;
        this.year2023.height = this.resources['2023'].texture.orig.height / 2;
        this.year2023.anchor.set(0.5, 1);
        this.year2023.x = this.titleScale === 1
            ? this.title.width / 4 + this.width / 2 + 30
            : this.width * .81;
        this.year2023.y = this.height / 2;
        this.year2023.alpha = 0;
        this.year2023.scale.set(0.1);
        this.year2023.zIndex = 2;
        this.scene.addChild(this.year2023);
    }

    resizeSnowfall() {
        this.ellipseCenterX = this.width / 2;
        this.ellipseCenterY = this.height / 2;
        this.ellipseHeight = this.height * 0.6;
        this.ellipseWidth = this.width * 0.8;
        this.fadeStartY = this.height - this.height * 0.1;
    }

    initSnowfall() {
        this.snowfall_max = 200;
        this.snowfall = [];
        this.snowfallContainer = new Container();
        this.radiusMin = 2;
        this.radiusMax = 5;

        this.ellipseCenterX = this.width / 2;
        this.ellipseCenterY = this.height / 2;
        this.ellipseHeight = this.height * 0.6;
        this.ellipseWidth = this.width * 0.8;

        for (let i = 0; i < this.snowfall_max; i++) {
            let graphics = new Graphics();
            graphics.beginFill(0xFFFFFF, 0.8); // rgba(255, 255, 255, 0.8)
            let radius = Math.random() * (this.radiusMax - this.radiusMin) + this.radiusMin;
            graphics.drawCircle(0, 0, radius);
            graphics.endFill();

            let particle = {
                graphics: graphics,
                x: Math.random() * this.width * 2 - this.width,
                y: Math.random() * this.height - this.height * 1.3,
                r: radius,
                d: Math.random() * this.snowfall_max,
            };

            this.snowfallContainer.addChild(graphics);
            this.snowfall.push(particle);
        }

        this.scene.addChild(this.snowfallContainer);
        this.snowfallContainer.zIndex = 3;

        let angle = 0;
        this.fadeStartY = this.height - this.height * 0.1;


        this.app.ticker.add(() => {
            angle += 0.005;

            this.snowfall.forEach(particle => {
                let p = particle;

                // Update position based on angle
                p.y += (Math.cos(angle + p.d) + 1 + p.r / 2) * .3;
                p.x += Math.sin(angle);

                // Check if the snowflake is inside the ellipse
                let dx = (p.x - this.ellipseCenterX) / (this.ellipseWidth / 2);
                let dy = (p.y - this.ellipseCenterY) / (this.ellipseHeight / 2);
                let isInEllipse = (dx * dx + dy * dy) <= 1;

                // Apply eased alpha for the ellipse area
                let easingRate = 0.01; // Smaller value for slower easing
                if (isInEllipse) {
                    p.graphics.alpha = Math.max(p.graphics.alpha - easingRate, 0.15); // Easing in to 0.15 opacity
                } else if (p.graphics.alpha < 1 && p.y <= this.fadeStartY) {
                    p.graphics.alpha = Math.min(p.graphics.alpha + easingRate, 1); // Easing out back to full opacity
                }

                // Calculate the fade for the bottom area if outside the ellipse
                if (!isInEllipse && p.y > this.fadeStartY) {
                    p.graphics.alpha = Math.max(p.graphics.alpha - easingRate, 0); // Easing out to 0 opacity
                }

                // Apply position
                p.graphics.x = p.x;
                p.graphics.y = p.y;

                // Reset position and alpha if snowflake falls off the bottom
                if (p.y > this.height + 30) {
                    p.x = Math.random() * this.width * 2 - this.width;
                    p.y = -50;
                    p.graphics.alpha = 1; // Reset alpha to full when recycling the snowflake
                }
            });
        });
    }

    calculateTitleScale() {
        const minWidth = 1100;

        if(this.width < minWidth) {
            this.titleScale = this.width / minWidth * .8;
        } else {
            this.titleScale = 1;
        }

        const scale = this.titleScale / 2;
        this.title.scale.set(scale);
        this.shadow.scale.set(scale);
        this.lettersContainer.scale.set(this.titleScale);
        this.glow.scale.set(scale);
    }

    addTitle() {
        this.title = new Sprite(this.resources.title.texture);
        this.title.width = this.resources.title.texture.orig.width / 2;
        this.title.height = this.resources.title.texture.orig.height / 2;
        this.title.anchor.set(0.5);
        this.title.x = this.width / 2;
        this.title.y = this.height / 2;
        this.title.alpha = 0;
        this.title.zIndex = 4;
        this.scene.addChild(this.title);
    }

    addGlow() {
        this.glow = new Sprite(this.resources.glow.texture);
        this.glow.width = this.resources.glow.texture.orig.width / 2;
        this.glow.height = this.resources.glow.texture.orig.height / 2;
        this.glow.anchor.set(0.5);
        this.glow.x = this.width / 2;
        this.glow.y = this.height / 2;
        this.glow.alpha = 0;
        this.glow.zIndex = 5;
        this.scene.addChild(this.glow);
    }

    addShadow() {
        this.shadow = new Sprite(this.resources.shadow.texture);
        this.shadow.width = this.resources.shadow.texture.orig.width / 2;
        this.shadow.height = this.resources.shadow.texture.orig.height / 2;
        this.shadow.anchor.set(0.5);
        this.shadow.x = this.width / 2;
        this.shadow.y = this.height / 2;
        this.shadow.alpha = 0;
        this.shadow.zIndex = 1;
        this.scene.addChild(this.shadow);
    }

    addShadowBg() {
        this.shadowBg = new Sprite(this.resources.shadowbg.texture);
        this.shadowBg.width = this.resources.shadowbg.texture.orig.width / 2;
        this.shadowBg.height = this.resources.shadowbg.texture.orig.height / 2;
        this.shadowBg.anchor.set(0.5);
        this.shadowBg.x = this.width / 2;
        this.shadowBg.y = this.height / 2;
        this.shadowBg.alpha = 0;
        this.shadowBg.zIndex = 0;
        this.scene.addChild(this.shadowBg);
    }

    initLetters() {
        this.lettersArray = [];

        const rect = new Graphics()
            .beginFill(0xaaffff, 0.1)
            .drawRect(0, 0, this.title.width, this.title.height)
            .endFill();

        const letterBounds = [
            { x: 189, y: 1, width: 236, height: 135, xc: 906, yc: 590 }, // 307+599 906, 590
            { x: 455, y: 1, width: 262, height: 135, xc: 1185, yc: 590 }, // 586+599 1185, 590
            { x: 747, y: 1, width: 200, height: 135, xc: 1446, yc: 590 }, // 847+599 1047, 590
            { x: 974, y: 1, width: 22, height: 135, xc: 1584, yc: 590 },  // 985+599 1584, 590
            { x: 1033, y: 1, width: 237, height: 135, xc: 1750, yc: 590 }, // 1151+599 1750, 590
            { x: 1278, y: 1, width: 253, height: 135, xc: 2004, yc: 590 }, // 1405+599 2004, 590
            { x: 1543, y: 1, width: 246, height: 135, xc: 2225, yc: 590 }, // 1626+599 2225, 590

            { x: 1, y: 195, width: 255, height: 135, xc: 727, yc: 784 }, // 128+599 727, 784
            { x: 288, y: 195, width: 22, height: 135, xc: 898, yc: 784 }, // 299+599 898, 784
            { x: 347, y: 195, width: 212, height: 135, xc: 1052, yc: 784 }, // 453+599 1052, 784
            { x: 576, y: 195, width: 223, height: 135, xc: 1285, yc: 784 }, // 686+599 1285, 784

            { x: 904, y: 195, width: 255, height: 135, xc: 1630, yc: 784 }, // 1031+599 1630, 784
            { x: 1187, y: 195, width: 243, height: 135, xc: 1908, yc: 784 }, // 1309+599 1908, 784
            { x: 1464, y: 195, width: 22, height: 135, xc: 2074, yc: 784 }, // 1475+599 2074, 784
            { x: 1523, y: 195, width: 237, height: 135, xc: 2240, yc: 784 }, // 1641+599 2240, 784
            { x: 1790, y: 195, width: 212, height: 135, xc: 2495, yc: 784 }, // 1896+599 2495, 784
        ];

        letterBounds.forEach((bound, index) => {
            let letterTexture = new Texture(this.resources.letters.texture, bound);
            let letterSprite = new Sprite(letterTexture);
            letterSprite.anchor.set(0.5);
            letterSprite.x = bound.xc / 2;
            letterSprite.y = bound.yc / 2 + bound.height / 2;
            letterSprite.originalWidth = bound.width / 2;
            letterSprite.originalHeight = bound.height / 2;
            letterSprite.width = bound.width / 2;
            letterSprite.height = bound.height / 2;
            letterSprite.alpha = 0; // initially invisible

            let letterMaskRect = new Graphics()
                .beginFill(0xffffff)
                .drawRect(bound.xc / 2, bound.yc / 2, bound.width / 2, bound.height / 2)
                .endFill();

            letterMaskRect.pivot.x = bound.width / 4;
            letterMaskRect.pivot.y = bound.height / 4;

            letterSprite.mask = letterMaskRect;

            this.lettersContainer.addChild(letterMaskRect);
            this.lettersContainer.addChild(letterSprite);
            this.lettersArray.push(letterSprite);
        });


        this.scene.addChild(this.lettersContainer);

        this.lettersContainer.x = this.title.x + 2;
        this.lettersContainer.y = this.title.y;
        this.lettersContainer.pivot.x = this.title.width / 2;
        this.lettersContainer.pivot.y = this.title.height / 2;
        this.lettersContainer.zIndex = 20;
    }

    animateGlow() {
        this.glowTimeline = gsap.timeline({
            repeat: -1,
            yoyo: true,
        })
            .to(this.glow, {
                alpha: 0.3,
                duration: 2,
                ease: "sine.out",
            })
    }

    animateLetters() {
        const lettersTimeline = gsap.timeline({
            onComplete: () => {
                this.animateGlow();
            }
        });

        this.lettersArray.forEach((letter, index) => {
            lettersTimeline.to(letter, {
                alpha: 1,
                y: letter.y - letter.height,
                duration: 0.5,
                ease: "power2.out",
            }, index * 0.07);
        })

        const totalDelay = this.lettersArray.length * 0.07 + 0.1;

        lettersTimeline.to(this.shadowBg, {
            alpha: 1,
            duration: 0.5,
            ease: "power2.out",
        }, totalDelay);

        lettersTimeline.to(this.title, {
            alpha: 1,
            duration: 0.5,
            ease: "power2.out",
        }, totalDelay);

        lettersTimeline.to(this.x1, {
            alpha: 1,
            duration: 0.5,
            ease: "power2.out",
        }, totalDelay);

        lettersTimeline.to(this.x2, {
            alpha: 1,
            duration: 0.5,
            ease: "power2.out",
        }, totalDelay + 0.25);

        lettersTimeline.to(this.x3, {
            alpha: 1,
            duration: 0.5,
            ease: "power2.out",
        }, totalDelay + 0.5);

        lettersTimeline.to(this.shadow, {
            alpha: 1,
            duration: 0.5,
            ease: "power2.out",
        }, totalDelay + 0.15);


        lettersTimeline.to(this.glow, {
            alpha: 1,
            duration: 0.5,
            ease: "power2.out",
        }, totalDelay + 0.3);

        lettersTimeline.to(this.lettersContainer, {
            alpha: 0,
            duration: 0.2,
            ease: "power2.out",
        }, totalDelay + 0.3);

        lettersTimeline.to(this.year2023, {
            alpha: 1,
            duration: 1.5,
            ease: "power2.out",
        }, totalDelay + 1);

        lettersTimeline.to(this.year2023.scale, {
            x: .5 * this.titleScale,
            y: .5 * this.titleScale,
            duration: 1.5,
            ease: "power2.out",
        }, totalDelay + 1);
    }

    addGradientBackground() {
        const canvas = document.createElement('canvas');
        const gradientWidth = Math.max(this.width, 1680);
        canvas.width = gradientWidth;
        canvas.height = this.height;
        const scaleY = this.height / gradientWidth;
        const inverseScaleY = gradientWidth / this.height;
        const ctx = canvas.getContext('2d');

        let gradient = ctx.createRadialGradient(
            gradientWidth / 2, this.height / 2 * inverseScaleY, 0, // inner circle
            gradientWidth / 2, this.height / 2 * inverseScaleY, gradientWidth / 2 // outer circle
        );
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(0.7917, '#000');

        ctx.fillStyle = gradient;
        ctx.setTransform(1, 0, 0, scaleY, 0, 0)
        ctx.fillRect(0, 0, gradientWidth, this.height * inverseScaleY);

        const texture = Texture.from(canvas);
        const sprite = new Sprite(texture);
        sprite.anchor.set(0.5);
        sprite.x = this.width / 2;
        sprite.y = this.height / 2;
        sprite.width = gradientWidth;
        sprite.height = this.height;

        this.gradientCanvas = canvas;
        this.gradientSprite = sprite;

        this.scene.addChild(sprite);
    }

    resizeGradientBackground() {
        if (!this.gradientSprite || !this.gradientCanvas) return;

        const gradientWidth = Math.max(this.width, 1680);
        this.gradientCanvas.width = gradientWidth;
        this.gradientCanvas.height = this.height;
        const scaleY = this.height / gradientWidth;
        const inverseScaleY = gradientWidth / this.height;
        const ctx = this.gradientCanvas.getContext('2d');

        let gradient = ctx.createRadialGradient(
            gradientWidth / 2, this.height / 2 * inverseScaleY, 0, // inner circle
            gradientWidth / 2, this.height / 2 * inverseScaleY, gradientWidth / 2 // outer circle
        );
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(0.7917, '#000');

        ctx.fillStyle = gradient;
        ctx.setTransform(1, 0, 0, scaleY, 0, 0);
        ctx.fillRect(0, 0, gradientWidth, this.height * inverseScaleY);

        const texture = Texture.from(this.gradientCanvas);
        this.gradientSprite.texture = texture;
        this.gradientSprite.x = this.width / 2;
        this.gradientSprite.y = this.height / 2;
        this.gradientSprite.width = gradientWidth;
        this.gradientSprite.height = this.height;
    }
}

export default HeroGiftGuide;
